import { matchPath } from 'react-router-dom';

import { NavbarFlags } from '@/types/navbarFlags';

import { NavItemType } from '@/@mantis/types/menu';
import { Building03Icon } from '@/assets/icons/Building03Icon';
import { CalendarDateIcon } from '@/assets/icons/CalendarDateIcon';
import { ChatIcon } from '@/assets/icons/ChatIcon';
import { ClipboardCheckIcon } from '@/assets/icons/ClipboardCheckIcon';
import { File04Icon } from '@/assets/icons/File04Icon';
import { GitMergeIcon } from '@/assets/icons/GitMergeIcon';
import { HomeLineIcon } from '@/assets/icons/HomeLineIcon';
import { Inbox01Icon } from '@/assets/icons/Inbox01Icon';
import { MessageSquare01Icon } from '@/assets/icons/MessageSquare01Icon';
import { PhoneIcon } from '@/assets/icons/PhoneIcon';
import { Speedometer01Icon } from '@/assets/icons/Speedometer01Icon';
import { Ticket02Icon } from '@/assets/icons/Ticket02Icon';
import { UserPlus01Icon } from '@/assets/icons/UserPlus01Icon';
import { Users01Icon } from '@/assets/icons/Users01Icon';
import { PMPRoutes } from '@/config/routes';
//
export const getPropertyManagerMenuItems = (
  isSingleFamilyAccount: boolean,
  isPropertyOwner: boolean,
  isAdmin: boolean,
  navbarFlags?: NavbarFlags,
  unreadInboxCount?: number
): NavItemType[] => {
  // Default to showing all items if flags are not available
  const flags = navbarFlags || {
    showCommunicationMenuItems: true,
    showWorkflowsMenuItem: true,
    showPropertyInvoices: true,
    showMaintenanceRequestsMenuItem: true,
  };

  const menuItems: NavItemType[] = [
    {
      id: 'dashboard-group',
      type: 'group',
      children: [
        {
          id: 'dashboard',
          title: 'Dashboard',
          type: 'item',
          icon: Speedometer01Icon,
          url: PMPRoutes.dashboard,
          description: 'View your property performance metrics and overall system status.',
          getIsSelected: pathname => PMPRoutes.dashboard === pathname,
        },
        {
          id: 'checklist',
          title: 'Checklist',
          type: 'item',
          url: PMPRoutes.onboardingChecklist,
          icon: ClipboardCheckIcon,
          description: 'View your onboarding checklist.',
          getIsSelected: pathname =>
            [PMPRoutes.onboardingChecklist].some(path => !!matchPath(pathname, path)),
        },
        {
          id: 'contacts',
          title: 'Contacts',
          type: 'item',
          icon: Users01Icon,
          url: PMPRoutes.contactList,
          description:
            'View and manage all contacts including tenants, vendors, and emergency contacts.',
          getIsSelected: pathname =>
            [
              PMPRoutes.contactList,
              PMPRoutes.contactCreate,
              PMPRoutes.contactView,
              PMPRoutes.contactEdit,
            ].some(path => !!matchPath(pathname, path)),
        },
        {
          id: 'inbox',
          title: 'Inbox',
          type: 'item',
          url: PMPRoutes.inbox,
          icon: Inbox01Icon,
          description: 'View and manage inbound email messages from sessions.',
          getIsSelected: pathname => [PMPRoutes.inbox].some(path => !!matchPath(pathname, path)),
          ...(unreadInboxCount &&
            unreadInboxCount > 0 && {
            chip: {
              label: unreadInboxCount.toString(),
              color: 'error',
              size: 'small',
              variant: 'filled',
            },
          }),
        },
      ],
    },
    ...(!isPropertyOwner
      ? [
        {
          id: 'properties',
          title: 'My Properties',
          type: 'group',
          description: 'Manage all your locations.',
          children: [
            {
              id: 'locations',
              title: 'Locations',
              type: 'item',
              icon: Building03Icon,
              url: PMPRoutes.propertyLocationList,
              description:
                  'View and manage all your property locations with detailed information.',
              getIsSelected: (pathname: string) =>
                [
                  PMPRoutes.propertyLocationList,
                  PMPRoutes.propertyLocationView,
                  PMPRoutes.propertyLocationManagerList,
                  PMPRoutes.propertyLocationViewRuleList,
                ].some(path => !!matchPath(pathname, path)),
            },
            ...(isSingleFamilyAccount
              ? []
              : [
                {
                  id: 'buildings',
                  title: 'All Buildings',
                  type: 'item',
                  icon: HomeLineIcon,
                  url: PMPRoutes.buildingList,
                  description:
                        'Access and manage all buildings, units, and tenants across your properties.',
                  getIsSelected: (pathname: string) =>
                    [
                      PMPRoutes.buildingList,
                      PMPRoutes.buildingView,
                      PMPRoutes.buildingViewDocumentList,
                      PMPRoutes.buildingViewUnitList,
                      PMPRoutes.unitView,
                      PMPRoutes.unitViewDocumentList,
                      PMPRoutes.unitViewKnowledgeList,
                      PMPRoutes.unitViewMessageList,
                      PMPRoutes.unitViewMessageView,
                      PMPRoutes.unitViewTenantList,
                      PMPRoutes.tenantView,
                      PMPRoutes.tenantViewDocumentList,
                    ].some(path => !!matchPath(pathname, path)),
                },
              ]),
          ],
        },
      ]
      : []),
  ];

  // Conditionally add Communications group based on flags
  if (flags.showCommunicationMenuItems) {
    menuItems.push({
      id: 'communications',
      title: 'Communications',
      type: 'group',
      description: 'Manage all communications with tenants, vendors, and other stakeholders.',
      children: [
        {
          id: 'messages',
          title: 'Inbound Messages',
          type: 'item',
          icon: MessageSquare01Icon,
          url: PMPRoutes.inboundMessages,
          description: 'View and respond to all incoming messages from tenants and vendors.',
          getIsSelected: pathname =>
            [
              PMPRoutes.inboundMessages,
              PMPRoutes.inboundMessagesWithTenants,
              PMPRoutes.inboundMessagesWithVendors,
            ].some(path => !!matchPath(pathname, path)),
        },
        {
          id: 'inbound-calls',
          title: 'Inbound Calls',
          type: 'item',
          url: PMPRoutes.inboundCallList,
          icon: PhoneIcon,
          description: 'Track and manage all incoming calls from tenants and vendors.',
          getIsSelected: pathname => PMPRoutes.inboundCallList === pathname,
        },
      ],
    });
  }

  // Build Operations Hub children array conditionally
  const operationsChildren: NavItemType[] = [];

  if (flags.showWorkflowsMenuItem) {
    operationsChildren.push({
      id: 'workflows',
      title: 'Workflows',
      type: 'item',
      icon: GitMergeIcon,
      url: PMPRoutes.workflowList,
      description: 'Configure and manage automated workflows for property management tasks.',
    });
  }

  if (flags.showPropertyInvoices) {
    operationsChildren.push({
      id: 'invoices',
      title: 'Property Invoices',
      type: 'item',
      url: PMPRoutes.invoiceList,
      icon: File04Icon,
      description: 'View, create, and manage all property-related invoices and payments.',
      getIsSelected: pathname => PMPRoutes.invoiceList === pathname,
    });
  }

  // Always show calendar and document vault
  operationsChildren.push(
    {
      id: 'calendar',
      title: 'Property Calendar',
      type: 'item',
      icon: CalendarDateIcon,
      url: PMPRoutes.calendar,
      description: 'Track important dates, appointments, and property-related events.',
      getIsSelected: pathname => PMPRoutes.calendar === pathname,
    },
    {
      id: 'vault',
      title: 'Document Vault',
      type: 'item',
      icon: File04Icon,
      url: PMPRoutes.vault,
      description: 'Access and manage all property-related documents and files securely.',
      getIsSelected: pathname => PMPRoutes.vault === pathname,
    }
  );

  if (flags.showMaintenanceRequestsMenuItem) {
    operationsChildren.push({
      id: 'tickets',
      title: 'Maintenance Requests',
      type: 'item',
      url: PMPRoutes.ticketList,
      icon: Ticket02Icon,
      description: 'Manage tenant maintenance requests and work orders for your properties.',
      getIsSelected: pathname => PMPRoutes.ticketList === pathname,
    });
  }

  // Add Operations Hub if there are any children
  if (operationsChildren.length > 0) {
    menuItems.push({
      id: 'operations',
      title: 'Operations Hub',
      type: 'group',
      description: 'Manage day-to-day property operations, maintenance, and documentation.',
      children: operationsChildren,
    });
  }

  // Add User Management group - only for admin users
  if (isAdmin) {
    menuItems.push({
      id: 'user-management',
      title: 'User Management',
      type: 'group',
      description: "Manage users and their access to this property's information.",
      children: [
        {
          id: 'invite-member',
          title: 'Invite User',
          type: 'item',
          icon: UserPlus01Icon,
          url: PMPRoutes.inviteHouseholdMember,
          description: 'Invite new users and assign different access levels to this property.',
          getIsSelected: pathname => PMPRoutes.inviteHouseholdMember === pathname,
        },
      ],
    });
  }

  menuItems.push({
    id: 'chat-with-ai',
    title: 'Ask AI',
    type: 'group',
    icon: ChatIcon,
    description: 'Ask our AI to get answers from your documents.',
    url: PMPRoutes.chatWithAI,
  });

  return menuItems;
};
